import { useState } from "react";
import { useReportFastFashion } from "@/hooks/useFastFashionReport";
import { toast } from "@/lib/toast";

interface Props {
  open: boolean;
  onClose: () => void;
  scanId: string;
}

export function ReportFastFashionDialog({ open, onClose, scanId }: Props) {
  const [reason, setReason] = useState("");
  const report = useReportFastFashion();

  if (!open) return null;

  const submit = async () => {
    try {
      await report.mutateAsync({ scanId, reason: reason || undefined });
      toast.success("Reported. 3 moderator flags will trigger admin review.");
      setReason("");
      onClose();
    } catch (err: any) {
      const msg = err?.message ?? "Couldn't submit report";
      // Common case: duplicate report (UNIQUE(reporter_id, scan_id))
      if (msg.includes("duplicate") || msg.includes("unique")) {
        toast.error("You already flagged this post");
      } else {
        toast.error(msg);
      }
    }
  };

  const ink = "hsl(var(--ink))";
  const paper = "hsl(var(--paper))";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ff-report-title"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "rgba(20, 14, 7, 0.6)",
        display: "flex",
        alignItems: "flex-end",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          background: paper,
          color: ink,
          padding: "20px 16px calc(env(safe-area-inset-bottom, 16px) + 16px)",
          borderTop: `1px solid ${ink}`,
        }}
      >
        <h2
          id="ff-report-title"
          style={{
            fontFamily: "'Fraunces', serif",
            fontWeight: 500,
            fontSize: 22,
            margin: 0,
            marginBottom: 6,
          }}
        >
          Report as fast fashion
        </h2>
        <p style={{ fontSize: 13, opacity: 0.7, marginBottom: 16, lineHeight: 1.5 }}>
          As a verified moderator, your flag counts toward auto-suspension. Three flags trigger fig admin review.
        </p>

        <label
          style={{
            display: "block",
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            marginBottom: 6,
            opacity: 0.7,
          }}
        >
          Reason (optional)
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={report.isPending}
          placeholder="What signals this as fast fashion?"
          rows={3}
          style={{
            width: "100%",
            padding: "10px 12px",
            border: `1px solid ${ink}`,
            background: paper,
            color: ink,
            marginBottom: 16,
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 13,
            resize: "vertical",
          }}
        />

        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={report.isPending}
            style={{
              flex: 1,
              padding: "12px",
              border: `1px solid ${ink}`,
              background: paper,
              color: ink,
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              cursor: report.isPending ? "wait" : "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={report.isPending}
            style={{
              flex: 1,
              padding: "12px",
              border: `1px solid ${ink}`,
              background: ink,
              color: paper,
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              cursor: report.isPending ? "wait" : "pointer",
              opacity: report.isPending ? 0.7 : 1,
            }}
          >
            {report.isPending ? "Reporting…" : "Flag fast fashion"}
          </button>
        </div>
      </div>
    </div>
  );
}
